import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, Globe, Users, Briefcase, Search, ChevronDown,
  Leaf, Laptop, Palette, MessageCircle, Heart, ClipboardList,
  BarChart3, Building2, BookOpen, Scale, Handshake, FileText,
  Languages, Calendar, Calculator, Apple, Lightbulb, Gavel,
  Newspaper, PieChart, UserCheck, Plane, AlertTriangle, Target,
  Cpu, ArrowRight, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const internshipPositions = [
  {
    id: 1,
    title: "Research & Policy Analysis Intern",
    icon: Search,
    description: "Support OPAD's research team with policy briefs, desk reviews, and comparative studies on poverty alleviation, SDGs, and development practices. Assist in drafting reports and preparing presentations.",
    capacity: 4,
    category: "Research"
  },
  {
    id: 2,
    title: "Climate Change & Environment Intern",
    icon: Leaf,
    description: "Assist in climate adaptation project design, carbon footprint analysis, and awareness campaigns. Conduct research on renewable energy and circular economy models in Africa & Asia.",
    capacity: 3,
    category: "Environment"
  },
  {
    id: 3,
    title: "Digital Library & E-Learning Intern (AfriBiblio Project)",
    icon: BookOpen,
    description: "Work on content management, digitization, and testing of OPAD's digital learning platform for African universities. Upload and categorize e-learning materials.",
    capacity: 4,
    category: "Education"
  },
  {
    id: 4,
    title: "ICT & Web Development Intern",
    icon: Laptop,
    description: "Assist with OPAD's website updates, coding small web applications, and maintaining online databases. Collaborate with IT officers to improve digital platforms.",
    capacity: 3,
    category: "Technology"
  },
  {
    id: 5,
    title: "Graphic Design & Multimedia Intern",
    icon: Palette,
    description: "Create infographics, digital posters, and video content for OPAD's campaigns. Support branding for events and publications.",
    capacity: 3,
    category: "Creative"
  },
  {
    id: 6,
    title: "Social Media & Communications Intern",
    icon: MessageCircle,
    description: "Manage OPAD's social media channels (WeChat, Facebook, LinkedIn, TikTok). Draft posts, monitor engagement, and support global campaigns.",
    capacity: 5,
    category: "Communications"
  },
  {
    id: 7,
    title: "Fundraising & Resource Mobilization Intern",
    icon: Heart,
    description: "Assist in donor mapping, drafting grant proposals, and preparing fundraising materials. Support team in crowdfunding initiatives.",
    capacity: 3,
    category: "Fundraising"
  },
  {
    id: 8,
    title: "Project Management Intern",
    icon: ClipboardList,
    description: "Provide administrative and logistical support for ongoing projects. Assist in documentation, monitoring, and scheduling project activities.",
    capacity: 4,
    category: "Management"
  },
  {
    id: 9,
    title: "Monitoring & Evaluation (M&E) Intern",
    icon: BarChart3,
    description: "Collect and analyze project data, update M&E frameworks, and support impact assessment reports.",
    capacity: 3,
    category: "Research"
  },
  {
    id: 10,
    title: "Community Development Intern",
    icon: Users,
    description: "Engage in community mobilization research, draft engagement strategies, and support OPAD's empowerment programs in Africa and Asia.",
    capacity: 4,
    category: "Development"
  },
  {
    id: 11,
    title: "Education & Training Intern",
    icon: GraduationCap,
    description: "Develop learning materials, assist in capacity-building workshops, and create youth empowerment curricula.",
    capacity: 3,
    category: "Education"
  },
  {
    id: 12,
    title: "Gender & Inclusion Intern",
    icon: Scale,
    description: "Support OPAD's feminist and gender equality programs. Conduct desk research on gender-responsive development. Assist in training design.",
    capacity: 2,
    category: "Social"
  },
  {
    id: 13,
    title: "Human Rights & Governance Intern",
    icon: Gavel,
    description: "Draft briefs on democracy, governance, and human rights. Assist in preparing advocacy materials and policy recommendations.",
    capacity: 3,
    category: "Governance"
  },
  {
    id: 14,
    title: "International Relations Intern",
    icon: Globe,
    description: "Track international cooperation opportunities, support partnerships with universities and NGOs, and help with MoUs.",
    capacity: 3,
    category: "Partnerships"
  },
  {
    id: 15,
    title: "Administrative Support Intern",
    icon: FileText,
    description: "Handle filing, correspondence, scheduling, and assisting OPAD's HR and admin teams.",
    capacity: 4,
    category: "Administration"
  },
  {
    id: 16,
    title: "Translation & Language Services Intern",
    icon: Languages,
    description: "Translate OPAD documents into Mandarin/English and support communications with Chinese partners.",
    capacity: 4,
    category: "Communications"
  },
  {
    id: 17,
    title: "Event Planning & Logistics Intern",
    icon: Calendar,
    description: "Support coordination of workshops, conferences, and online events. Manage logistics, invitations, and follow-up reporting.",
    capacity: 3,
    category: "Events"
  },
  {
    id: 18,
    title: "Finance & Accounting Intern",
    icon: Calculator,
    description: "Assist in financial reporting, bookkeeping, and grant budgeting under OPAD projects.",
    capacity: 2,
    category: "Finance"
  },
  {
    id: 19,
    title: "Health & Nutrition Intern",
    icon: Apple,
    description: "Support health-related projects (malnutrition, community clinics, maternal health). Conduct desk reviews on global health challenges.",
    capacity: 2,
    category: "Health"
  },
  {
    id: 20,
    title: "Youth Empowerment Intern",
    icon: Users,
    description: "Help design leadership training programs for youth. Support peer-to-peer learning initiatives and workshops.",
    capacity: 4,
    category: "Youth"
  },
  {
    id: 21,
    title: "Agriculture & Food Security Intern",
    icon: Leaf,
    description: "Conduct research on sustainable farming models, food distribution, and small-scale enterprise development.",
    capacity: 2,
    category: "Agriculture"
  },
  {
    id: 22,
    title: "Entrepreneurship & Innovation Intern",
    icon: Lightbulb,
    description: "Support incubation of youth-led businesses, social entrepreneurship models, and research on African start-up ecosystems.",
    capacity: 3,
    category: "Innovation"
  },
  {
    id: 23,
    title: "Legal & Compliance Intern",
    icon: Gavel,
    description: "Assist in reviewing partnership agreements, compliance documents, and provide legal research support.",
    capacity: 2,
    category: "Legal"
  },
  {
    id: 24,
    title: "Media & Journalism Intern",
    icon: Newspaper,
    description: "Write articles, reports, and human-interest stories about OPAD's work. Support production of newsletters.",
    capacity: 3,
    category: "Media"
  },
  {
    id: 25,
    title: "Data Analysis & Statistics Intern",
    icon: PieChart,
    description: "Support survey design, analyze project data with SPSS/Excel, and prepare visual reports.",
    capacity: 3,
    category: "Research"
  },
  {
    id: 26,
    title: "Volunteer Management Intern",
    icon: UserCheck,
    description: "Assist HR in recruitment, training, and monitoring of OPAD volunteers worldwide.",
    capacity: 3,
    category: "Management"
  },
  {
    id: 27,
    title: "Cultural Exchange & Partnership Intern",
    icon: Plane,
    description: "Facilitate student exchange, intercultural training, and bridge partnerships between Chinese and African institutions.",
    capacity: 2,
    category: "Partnerships"
  },
  {
    id: 28,
    title: "Humanitarian Aid & Emergency Relief Intern",
    icon: AlertTriangle,
    description: "Research disaster response models, help in relief campaign planning, and support logistics for aid operations.",
    capacity: 2,
    category: "Humanitarian"
  },
  {
    id: 29,
    title: "Sustainable Development Goals (SDGs) Intern",
    icon: Target,
    description: "Map OPAD activities against SDGs, prepare progress reports, and draft advocacy materials.",
    capacity: 3,
    category: "Development"
  },
  {
    id: 30,
    title: "Innovation & Technology for Development Intern",
    icon: Cpu,
    description: "Explore emerging tech (AI, blockchain, IoT) for poverty alleviation. Research case studies and propose pilot projects.",
    capacity: 3,
    category: "Technology"
  }
];

const categories = [
  "All",
  "Research",
  "Technology",
  "Communications",
  "Development",
  "Education",
  "Environment",
  "Management",
  "Creative",
  "Health",
  "Finance",
  "Legal"
];

export default function ChinaInternshipPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredPositions = internshipPositions.filter(position => {
    const matchesSearch = position.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         position.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || position.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const totalCapacity = internshipPositions.reduce((sum, pos) => sum + pos.capacity, 0);

  return (
    <div data-testid="china-internship-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E08B7A] via-[#E99A8A] to-[#F0A99A]">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-white/20 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/30 rounded-tr-[180px]" />
          <div className="absolute top-[30%] left-[10%] w-[150px] h-[150px] bg-[#7BC4E2]/20 rounded-full" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <span className="text-2xl">🇨🇳</span>
                <span className="text-white/90 text-sm font-medium">2026 Programme Announcement</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-['Manrope'] text-white mb-6">
                OPAD Internship Opportunities
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-4">
                For Students in China - 2026
              </p>
              <p className="text-base text-white/80 max-w-3xl mx-auto">
                The Organization for Poverty Alleviation and Development (OPAD) is offering a wide range of 
                internship opportunities for students in China. These roles provide practical experience in 
                international development, research, ICT, humanitarian aid, and organizational management.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="relative py-8 -mt-6 z-20">
        <div className="container-custom">
          <motion.div
            className="bg-white rounded-2xl shadow-xl p-6 grid grid-cols-3 gap-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#E08B7A] mb-1">30</div>
              <div className="text-slate-600 text-sm">Positions Available</div>
            </div>
            <div className="text-center border-x border-slate-200">
              <div className="text-3xl md:text-4xl font-bold text-[#4A7C59] mb-1">{totalCapacity}</div>
              <div className="text-slate-600 text-sm">Total Intern Slots</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-[#7BC4E2] mb-1">12+</div>
              <div className="text-slate-600 text-sm">Categories</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="relative py-8 overflow-hidden">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-full"
                data-testid="search-positions"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.slice(0, 8).map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-[#E08B7A] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Positions Grid */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-[#FDF8F4]">
          <div className="absolute top-0 left-0 w-[400px] h-[350px] bg-[#7BC4E2]/10 rounded-br-[180px]" />
          <div className="absolute bottom-0 right-0 w-[350px] h-[300px] bg-[#E08B7A]/10 rounded-tl-[150px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div 
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-2">
              Available Positions
            </h2>
            <p className="text-slate-600">
              Showing {filteredPositions.length} of {internshipPositions.length} positions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPositions.map((position, index) => (
              <motion.div
                key={position.id}
                className="bg-white/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-slate-100 hover:shadow-xl transition-all"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                data-testid={`position-${position.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-[#E08B7A]/20 rounded-xl flex items-center justify-center">
                    <position.icon className="w-6 h-6 text-[#E08B7A]" />
                  </div>
                  <span className="bg-[#4A7C59]/20 text-[#4A7C59] text-xs font-semibold px-3 py-1 rounded-full">
                    {position.capacity} slots
                  </span>
                </div>
                <h3 className="text-lg font-bold font-['Manrope'] text-slate-900 mb-2">
                  {position.id}. {position.title}
                </h3>
                <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                  {position.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                    {position.category}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-white">
          <div className="absolute top-0 right-0 w-[500px] h-[450px] bg-[#4A7C59]/10 rounded-bl-[250px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[350px] bg-[#E5B75F]/10 rounded-tr-[180px]" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-[#4A7C59]/20 rounded-full px-4 py-2 mb-4">
              <Briefcase className="w-5 h-5 text-[#4A7C59]" />
              <span className="text-[#4A7C59] text-sm font-semibold">How to Apply</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-slate-600 mb-8">
              Submit your application through our online internship portal. Include your CV, 
              motivation letter, and indicate your preferred position(s). Applications are 
              reviewed on a rolling basis.
            </p>
            <Link to="/internship#apply">
              <Button className="bg-[#E08B7A] hover:bg-[#D07A69] text-white rounded-full px-8 py-6 font-semibold text-lg">
                <GraduationCap className="w-5 h-5 mr-2" />
                Apply Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Location Info */}
      <section className="relative section-padding overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7BC4E2] via-[#8BCDE8] to-[#9BD6EE]">
          <div className="absolute top-0 right-0 w-[400px] h-[350px] bg-white/15 rounded-bl-[200px]" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[250px] bg-[#4A7C59]/20 rounded-tr-[150px]" />
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <MapPin className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
              Remote & Flexible
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-4">
              Most internships can be conducted remotely, with opportunities for 
              virtual collaboration with OPAD teams across Africa, Europe, and Asia.
            </p>
            <p className="text-white/80">
              Join a global community of young leaders making a difference in humanitarian 
              and development work.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
